import { i18n } from '@quadralize/i18n'
import { cn } from '@quadralize/utils'
import { AltArrowLeft, AltArrowRight, CloseCircle, Logout, Restart } from '@solar-icons/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ScrollArea } from '../../data-display/scroll-area/scroll-area'
import type { NavItem, SidebarProps } from '../types'

const SIDEBAR_WIDTH = 256
const SIDEBAR_COLLAPSED_WIDTH = 80

export function Sidebar({
  className,
  isMobile,
  isCollapsed,
  onToggleCollapse,
  currentPath,
  items,
  bottomItems = [],
  onNavigate,
  onClose,
  onSignOut,
  isSignOutLoading,
  onUpdate,
  isUpdateAvailable,
  logo,
}: SidebarProps) {
  const { t } = useTranslation('ui', { i18n })
  const LogoIcon = logo?.icon

  return (
    <motion.aside
      initial={false}
      animate={{
        width: isMobile ? SIDEBAR_WIDTH : isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
      }}
      className={cn(
        'flex flex-col border-r bg-bg-subtle border-border-default relative overflow-visible md:z-50',
        isMobile ? 'h-dvh' : 'h-full',
        className,
      )}
      transition={{ type: 'spring', duration: 0.4, bounce: 0.1 }}
    >
      {!isMobile && onToggleCollapse && (
        <motion.button
          type="button"
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? t('sidebar.toggleExpand') : t('sidebar.toggleCollapse')}
          title={isCollapsed ? t('sidebar.toggleExpand') : t('sidebar.toggleCollapse')}
          className="absolute top-8 -translate-y-1/2 -right-3 z-20 flex h-10 w-6 items-center justify-center rounded-full border border-border-subtle bg-bg-base text-text-secondary shadow-md transition-colors hover:bg-bg-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
          initial={false}
          animate={{ opacity: 1 }}
          whileTap={{ scale: 0.96 }}
        >
          {isCollapsed ? (
            <AltArrowRight className="h-4 w-4" />
          ) : (
            <AltArrowLeft className="h-4 w-4" />
          )}
        </motion.button>
      )}

      <div
        className={cn(
          'flex h-16 items-center shrink-0 border-b border-border-subtle overflow-hidden',
          isCollapsed && !isMobile ? 'justify-center px-2' : 'px-4',
        )}
      >
        <motion.div layout="position" className={cn('flex items-center', !isCollapsed && 'gap-3')}>
          {logo?.imageSrc ? (
            <img src={logo.imageSrc} alt={logo.text} className="h-8 w-8 min-w-8 shrink-0" />
          ) : LogoIcon ? (
            <div className="flex h-8 w-8 min-w-8 items-center justify-center rounded-lg bg-primary text-primary-fg shrink-0 shadow-sm">
              <LogoIcon weight="Bold" className="h-5 w-5" />
            </div>
          ) : null}
          <AnimatePresence mode="wait">
            {(!isCollapsed || isMobile) && logo?.text ? (
              <motion.span
                key="logo-text"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="text-xl font-bold tracking-tight text-text-primary whitespace-nowrap"
              >
                {logo.text}
              </motion.span>
            ) : null}
          </AnimatePresence>
        </motion.div>

        {isMobile && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-text-tertiary hover:text-text-primary transition-colors"
          >
            <CloseCircle className="h-5 w-5" />
          </button>
        )}
      </div>

      <ScrollArea type="hover" scrollbarSize="sm" className="flex-1">
        <nav className="flex flex-col gap-1 px-3 py-4">
          {items.map((entry, index) => {
            if ('items' in entry) {
              return (
                <div key={entry.title} className={cn('flex flex-col gap-1', index > 0 && 'pt-4')}>
                  <AnimatePresence mode="wait">
                    {(!isCollapsed || isMobile) && (
                      <motion.h4
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="px-3 text-[11px] font-bold uppercase tracking-wider text-text-tertiary/60"
                      >
                        {entry.title}
                      </motion.h4>
                    )}
                  </AnimatePresence>
                  <div className="flex flex-col gap-1">
                    {entry.items.map((item) => {
                      const isActive = currentPath === item.path
                      return (
                        <NavItemRenderer
                          key={item.path}
                          item={item}
                          isActive={isActive}
                          isCollapsed={isCollapsed}
                          isMobile={isMobile}
                          onNavigate={onNavigate}
                        />
                      )
                    })}
                  </div>
                </div>
              )
            }

            // It's a flat item
            const isActive = currentPath === entry.path
            return (
              <NavItemRenderer
                key={entry.path}
                item={entry}
                isActive={isActive}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                onNavigate={onNavigate}
              />
            )
          })}
        </nav>
      </ScrollArea>

      <div className="mt-auto border-t border-border-subtle p-3 pb-[calc(env(safe-area-inset-bottom)+12px)] flex flex-col gap-1 overflow-hidden shrink-0">
        {bottomItems.map((item) => (
          <button
            key={item.path}
            type="button"
            onClick={() => onNavigate(item.path)}
            className={cn(
              'flex h-11 w-full items-center rounded-xl transition-colors relative overflow-hidden',
              isCollapsed && !isMobile ? 'justify-center' : 'px-3 gap-3',
              'text-text-secondary hover:bg-bg-muted hover:text-text-primary',
            )}
            title={isCollapsed ? item.label : undefined}
          >
            {(() => {
              const Icon = item.icon
              return <Icon className="h-5 w-5 text-text-tertiary shrink-0" />
            })()}
            <AnimatePresence mode="wait">
              {!isCollapsed || isMobile ? (
                <motion.span
                  key={item.label}
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="whitespace-nowrap text-sm font-medium overflow-hidden"
                >
                  {item.label}
                </motion.span>
              ) : null}
            </AnimatePresence>
          </button>
        ))}

        <AnimatePresence>
          {isUpdateAvailable && onUpdate && (
            <motion.button
              type="button"
              onClick={onUpdate}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 44 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              className={cn(
                'flex h-11 w-full items-center rounded-xl transition-colors relative overflow-hidden',
                isCollapsed && !isMobile ? 'justify-center' : 'px-3 gap-3',
                'text-primary hover:bg-primary/10',
              )}
              title={isCollapsed ? t('sidebar.updateTooltip') : undefined}
            >
              <div className="relative flex h-5 w-5 items-center justify-center shrink-0">
                <Restart className="h-5 w-5" />
              </div>
              <AnimatePresence mode="wait">
                {!isCollapsed || isMobile ? (
                  <motion.span
                    key="update-text"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="whitespace-nowrap text-sm font-medium overflow-hidden"
                  >
                    {t('sidebar.update')}
                  </motion.span>
                ) : null}
              </AnimatePresence>
            </motion.button>
          )}
        </AnimatePresence>

        {onSignOut && (
          <button
            type="button"
            onClick={onSignOut}
            disabled={isSignOutLoading}
            className={cn(
              'flex h-11 w-full items-center rounded-xl transition-colors relative overflow-hidden',
              isCollapsed && !isMobile ? 'justify-center' : 'px-3 gap-3',
              'text-error hover:bg-error-subtle disabled:opacity-50 disabled:cursor-not-allowed',
            )}
            title={isCollapsed ? t('sidebar.signOutTooltip') : undefined}
          >
            <div className="relative flex h-5 w-5 items-center justify-center shrink-0">
              {isSignOutLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Logout className="h-5 w-5" />
              )}
            </div>
            <AnimatePresence mode="wait">
              {!isCollapsed || isMobile ? (
                <motion.span
                  key="logout-text"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="whitespace-nowrap text-sm font-medium overflow-hidden"
                >
                  {isSignOutLoading ? t('sidebar.signingOut') : t('sidebar.signOut')}
                </motion.span>
              ) : null}
            </AnimatePresence>
          </button>
        )}
      </div>
    </motion.aside>
  )
}

function NavItemRenderer({
  item,
  isActive,
  isCollapsed,
  isMobile,
  onNavigate,
}: {
  item: NavItem
  isActive: boolean
  isCollapsed: boolean | undefined
  isMobile: boolean | undefined
  onNavigate: (path: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.path)}
      className={cn(
        'group flex h-10 w-full items-center rounded-xl transition-colors relative overflow-hidden',
        isCollapsed && !isMobile ? 'justify-center' : 'px-3 gap-3',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-text-secondary hover:bg-bg-muted hover:text-text-primary',
      )}
      title={isCollapsed ? item.label : undefined}
    >
      {(() => {
        const Icon = item.icon
        return (
          <Icon
            className={cn(
              'h-5 w-5 shrink-0',
              isActive ? 'text-primary' : 'text-text-tertiary group-hover:text-text-secondary',
            )}
            weight={isActive ? 'Bold' : 'Outline'}
          />
        )
      })()}
      <AnimatePresence mode="wait">
        {!isCollapsed || isMobile ? (
          <motion.span
            key={item.label}
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className="whitespace-nowrap text-sm font-medium overflow-hidden"
          >
            {item.label}
          </motion.span>
        ) : null}
      </AnimatePresence>
    </button>
  )
}
